import { useMemo, SetStateAction, Dispatch } from 'react';
import { useIntl } from 'react-intl';

import { openModal } from 'App/ModalContext/utils';
import { useOrderMetadataMutation } from 'App/redux/MetadataApi';
import in_ from '_common/utils/in';
import { AUTOMATION_TRIGGER_TRANSLATIONS } from '../const';

import {
  Table as BaseTable,
  Cell,
  type TableColumn,
  type TableRow,
} from '_common/components/Table';

type TableProps = {
  metadata: ApiSchemas['MetadataObjectSchema'][];
  selectedMetadata: ApiSchemas['MetadataObjectSchema'] | undefined;
  deleteMetadata: (id: string, name: string) => void;
  select: Dispatch<SetStateAction<number>>;
};

type MetadataRow = ApiSchemas['MetadataObjectSchema'];

const Table = ({ metadata, selectedMetadata, deleteMetadata, select }: TableProps) => {
  const intl = useIntl();

  const [orderMetadata] = useOrderMetadataMutation();

  const edit = (metadata: ApiSchemas['MetadataObjectSchema']) => {
    openModal({
      modal: 'AutomaticMetadataModal',
      data: { metadata },
    });
  };

  const handleMoveUp = (metadataId: string) => {
    orderMetadata({ object_id: metadataId, direction: 'up' });
  };
  const handleMoveDown = (metadataId: string) => {
    orderMetadata({ object_id: metadataId, direction: 'down' });
  };

  const columns = useMemo<TableColumn<MetadataRow>[]>(() => {
    return [
      {
        id: 'name',
        accessorKey: 'name',
        enableSorting: false,
        minWidth: 240,
        header: intl.formatMessage({ id: 'global.name' }),
        cell: (info) => {
          const metadata = info.row.original;
          return <Cell testId={`${metadata.id}-name-column`}>{metadata.name}</Cell>;
        },
      },
      {
        id: 'automationTrigger',
        accessorKey: 'trigger',
        enableSorting: false,
        size: 200,
        header: intl.formatMessage({ id: 'AUTOMATION_TRIGGER' }),
        cell: (info) => {
          const metadata = info.row.original;

          return (
            <Cell testId={`${metadata.id}-trigger-column`}>
              {/* @ts-expect-error Metadata specs issue - trigger not a proprerty */}
              {in_(metadata.trigger, AUTOMATION_TRIGGER_TRANSLATIONS)
                ? // @ts-expect-error Metadata specs issue - trigger not a proprerty
                  AUTOMATION_TRIGGER_TRANSLATIONS[metadata.trigger]
                : // @ts-expect-error Metadata specs issue - trigger not a proprerty
                  metadata.trigger}
            </Cell>
          );
        },
      },
      {
        id: 'required',
        accessorKey: 'required',
        enableSorting: false,
        size: 160,
        header: intl.formatMessage({ id: 'REQUIRED' }),
        cell: (info) => {
          const metadata = info.row.original;
          return (
            <Cell testId={`${metadata.id}-required-column`}>
              <Cell.Boolean value={metadata.required} testId={`${metadata.id}-required`} />
            </Cell>
          );
        },
      },
    ];
  }, []);

  const rowsData = useMemo(() => {
    return metadata.map<TableRow<MetadataRow>>((m, index) => ({
      ...m,
      suffix: {
        actions: [
          {
            label: intl.formatMessage({ id: 'MOVE_METADATA_UP' }),
            disabled: index === 0,
            onClick: () => {
              handleMoveUp(m.id);
            },
          },
          {
            label: intl.formatMessage({ id: 'MOVE_METADATA_DOWN' }),
            disabled: index === metadata.length - 1,
            onClick: () => {
              handleMoveDown(m.id);
            },
          },
          {
            label: intl.formatMessage({ id: 'EDIT_METADATA' }),
            onClick: () => {
              edit(m);
            },
          },
          {
            label: intl.formatMessage({ id: 'DELETE_METADATA' }),
            onClick: () => {
              deleteMetadata(m.id, m.name);
            },
          },
        ],
      },
    }));
  }, [metadata]);

  return (
    <BaseTable
      testId="automatic-metadata-table"
      options={{ columns, data: rowsData }}
      onRowClick={({ index }) => {
        select(index);
      }}
      maxHeight="75rem"
      selection={selectedMetadata ? { [selectedMetadata.id]: true } : {}}
      identity="automatic_metadata"
      isLoading={false}
      loadingLabel={intl.formatMessage({ id: 'LOADING_ELEMENTS' })}
      renderFooter={() => null}
      selectable={false}
    />
  );
};

export default Table;
